import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme/mui-theme";

import { AuthProvider } from "./contexts/AuthContext";
import { AdminProvider } from "./contexts/AdminContext";
import { RestaurantProvider } from "./contexts/RestaurantContext";
import ProtectedRoute from "./components/ui/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Companies from "./pages/Companies";
import MenuEngineering from "./pages/MenuEngineering";
import InventoryIntelligence from "./pages/InventoryIntelligence";
import SupplierRelationshipOptimizer from "./pages/SupplierRelationshipOptimizer";
import Recipes from "./pages/recipes/Recipes";
import RecipeDetail from "./pages/recipes/RecipeDetail";
import AIAssistant from "./pages/AIAssistant";
import AnalysisInsights from "./pages/AnalysisInsights";
import AnalysisInsightDetail from "./pages/AnalysisInsightDetail";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import MainLayout from "./components/ui/sidebar/MainLayout";
import DynamicPricingEngine from "./pages/DynamicPricingEngine";
import WasteReductionAnalyzer from "./pages/WasteReductionAnalyzer";
import ProcurementCalendarOptimizer from "./pages/ProcurementCalendarOptimizer";
import RecipeInnovation from "./pages/RecipeInnovation";
import OperationalEfficiencyAnalyzer from "./pages/OperationalEfficiencyAnalyzer";
import FinancialPerformanceForecaster from "./pages/FinancialPerformanceForecaster";

// Wrapper component to handle recipe redirects with URL parameters
function RecipeRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/recipes/${id}`} replace />;
}

// Wrapper component to handle analysis redirects with URL parameters
function AnalysisRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/analysis/${id}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <RestaurantProvider>
        <AdminProvider>
          <ThemeProvider theme={theme}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Protected routes */}
            <Route 
              path="/app" 
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="companies" element={<Companies />} />
              <Route path="menu-engineering" element={<MenuEngineering />} />
              <Route path="inventory-intelligence" element={<InventoryIntelligence />} />
              <Route path="supplier-relationship" element={<SupplierRelationshipOptimizer />} />
              <Route path="dynamic-pricing" element={<DynamicPricingEngine />} />
              <Route path="waste-reduction" element={<WasteReductionAnalyzer />} />
              <Route path="procurement-calendar" element={<ProcurementCalendarOptimizer />} />
              <Route path="recipes" element={<Recipes />} />
              <Route path="recipes/:id" element={<RecipeDetail />} />
              <Route path="recipe-innovation" element={<RecipeInnovation />} />
              <Route path="operational-efficiency" element={<OperationalEfficiencyAnalyzer />} />
              <Route path="financial-performance" element={<FinancialPerformanceForecaster />} />
              <Route path="ai-assistant" element={<AIAssistant />} />
              <Route path="analysis" element={<AnalysisInsights />} />
              <Route path="analysis/:id" element={<AnalysisInsightDetail />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            
            {/* Legacy routes redirects for backward compatibility */}
            <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/companies" element={<Navigate to="/app/companies" replace />} />
            <Route path="/menu-engineering" element={<Navigate to="/app/menu-engineering" replace />} />
            <Route path="/inventory-intelligence" element={<Navigate to="/app/inventory-intelligence" replace />} />
            <Route path="/supplier-relationship" element={<Navigate to="/app/supplier-relationship" replace />} />
            <Route path="/dynamic-pricing" element={<Navigate to="/app/dynamic-pricing" replace />} />
            <Route path="/waste-reduction" element={<Navigate to="/app/waste-reduction" replace />} />
            <Route path="/procurement-calendar" element={<Navigate to="/app/procurement-calendar" replace />} />
            <Route path="/recipes" element={<Navigate to="/app/recipes" replace />} />
            <Route path="/recipes/:id" element={<RecipeRedirect />} />
            <Route path="/recipe-innovation" element={<Navigate to="/app/recipe-innovation" replace />} />
            <Route path="/operational-efficiency" element={<Navigate to="/app/operational-efficiency" replace />} />
            <Route path="/financial-performance" element={<Navigate to="/app/financial-performance" replace />} />
            <Route path="/ai-assistant" element={<Navigate to="/app/ai-assistant" replace />} />
            <Route path="/analysis" element={<Navigate to="/app/analysis" replace />} />
            <Route path="/analysis/:id" element={<AnalysisRedirect />} />
            <Route path="/reports" element={<Navigate to="/app/reports" replace />} />
            <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
            
            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster richColors closeButton position="top-right" />
          </ThemeProvider>
        </AdminProvider>
      </RestaurantProvider>
    </AuthProvider>
  );
}
